import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Brain, PaperPlaneTilt, Sparkle } from '@phosphor-icons/react';
import api from '@/lib/api';

const suggestedQuestions = [
  "What are the top strategies to increase our network conversion rate?",
  "Predict next month's revenue based on current trends",
  "Which traffic sources have the highest ROI potential?",
  "How can we reduce fraud while maintaining traffic volume?",
  "Recommend the optimal affiliate payout structure for maximum growth",
];

export default function AIInsights() {
  const [question, setQuestion] = useState('');
  const [insight, setInsight] = useState('');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  const askAI = async (q) => {
    const query = q || question;
    if (!query.trim()) return;
    setLoading(true);
    setInsight('');
    try {
      const { data } = await api.post('/ai/insights', { question: query });
      setInsight(data.insight);
      setHistory(prev => [{ question: query, answer: data.insight, time: new Date().toLocaleTimeString() }, ...prev].slice(0, 10));
    } catch (err) {
      setInsight('Failed to get AI insights. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="ai-insights">
      <div>
        <h1 className="font-heading text-3xl font-bold text-white tracking-tight flex items-center gap-3">
          <Brain size={32} className="text-blue-400" weight="duotone" />
          AI Revenue Intelligence
        </h1>
        <p className="text-sm text-zinc-500 mt-1">GPT-5.2 powered insights for revenue optimization</p>
      </div>

      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-5">
          <div className="flex gap-3">
            <input
              data-testid="ai-question-input"
              value={question}
              onChange={e => setQuestion(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && askAI()}
              placeholder="Ask about revenue optimization, traffic analysis, affiliate performance..."
              className="flex-1 bg-zinc-950 border border-zinc-800 rounded-lg px-4 py-3 text-zinc-100 text-sm placeholder-zinc-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-colors"
            />
            <Button
              data-testid="ai-submit-btn"
              onClick={() => askAI()}
              disabled={loading || !question.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 gap-2"
            >
              {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <PaperPlaneTilt size={18} />}
              {loading ? 'Analyzing...' : 'Ask AI'}
            </Button>
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            {suggestedQuestions.map((q, i) => (
              <button
                key={i}
                data-testid={`suggested-q-${i}`}
                onClick={() => { setQuestion(q); askAI(q); }}
                className="px-3 py-1.5 bg-zinc-800/60 hover:bg-zinc-800 border border-zinc-700/50 rounded-lg text-xs text-zinc-400 hover:text-zinc-200 transition-colors flex items-center gap-1.5"
              >
                <Sparkle size={12} className="text-blue-400" />
                {q.length > 50 ? q.slice(0, 50) + '...' : q}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {(insight || loading) && (
        <Card className="bg-zinc-900 border-zinc-800 border-l-4 border-l-blue-500">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base text-white flex items-center gap-2">
              <Brain size={18} className="text-blue-400" />
              AI Analysis
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center gap-3 py-4">
                <div className="w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                <span className="text-sm text-zinc-400">Analyzing platform data with GPT-5.2...</span>
              </div>
            ) : (
              <div className="prose prose-invert prose-sm max-w-none" data-testid="ai-response">
                <div className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">{insight}</div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {history.length > 0 && (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base text-white">Recent Queries</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {history.map((h, i) => (
                <div key={i} className="p-3 bg-zinc-950 rounded-lg border border-zinc-800" data-testid={`history-item-${i}`}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs text-blue-400 font-medium">{h.question}</p>
                    <span className="text-[10px] text-zinc-600">{h.time}</span>
                  </div>
                  <p className="text-xs text-zinc-400 line-clamp-2">{h.answer}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
